import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateContext } from "../../contexts/CreateContext";
import useHubManagers from "../../hooks/useHubManagers";
import useCodeCommon from "../../hooks/useCodeCommon";
import useHubs from "../../hooks/useHubs";
import { getHubId } from "../../utils/cookieUtils";
import { useAppSelector } from "../../hooks/useAuth";
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../../components/ui/Pagination";
import SortableTable, {
  SortableColumn,
} from "../../components/common/SortableTable";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import CreateHubPage from "./Create-Hub/CreateHubPage";
import EditIcon from "../../../src/assets/icons/EditIcon.svg";
import DeleteIcon from "../../../src/assets/icons/DeleteIcon.svg";
import ActionIcon from "../../../src/assets/icons/ActionIcon.svg";
import apiService from "../../services/api";
import CustomLoading from "../../components/common/CustomLoading";

interface HubData {
  id: string;
  hubName: string;
  hubManager: string;
  mobileNo: string;
  city: string;
  status: string;
  operatingHours: string;
  totalVehicles: number;
  availableVehicles: number;
  bookedVehicles: number;
  maintenanceVehicles: number;
}

const StatusChip = ({
  status,
  statusOptions,
}: {
  status: string;
  statusOptions: any[];
}) => {
  const getStatusText = (status: string) => {
    const statusOption = statusOptions.find(
      (option) => option.codevalue === String(status),
    );
    return statusOption
      ? statusOption.codedisplayname
      : String(status) || "Active";
  };

  const statusText = getStatusText(status);

  const styles: Record<string, any> = {
    Active: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
    Inactive: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
    Pending: { bg: "rgba(255, 245, 157, 1)", color: "rgba(107, 105, 14, 1)" },
    Suspended: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
  };

  return (
    <Chip
      label={statusText}
      size="medium"
      sx={{
        backgroundColor: styles[statusText]?.bg,
        color: styles[statusText]?.color,
        fontWeight: 600,
      }}
    />
  );
};

const HubManagementPage: React.FC = () => {
  const [hubs, setHubs] = useState<HubData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Restore state from location if available
  const getInitialSearchTerm = () => (location.state as any)?.searchTerm || "";
  const getInitialCity = () => (location.state as any)?.city || "";
  const getInitialStatus = () => (location.state as any)?.status || "";
  const getInitialHubManager = () => (location.state as any)?.hubManager || "";
  const getInitialHub = () => (location.state as any)?.hub || "";
  const getInitialPage = () => (location.state as any)?.page || 0;

  const [searchTerm, setSearchTerm] = useState(getInitialSearchTerm());
  const [city, setCity] = useState(getInitialCity());
  const [status, setStatus] = useState(getInitialStatus());
  const [hubManager, setHubManager] = useState(getInitialHubManager());
  const [hub, setHub] = useState(getInitialHub());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hubToDelete, setHubToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [assignManagerModalOpen, setAssignManagerModalOpen] = useState(false);
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);
  const [selectedManagerId, setSelectedManagerId] = useState<string>("");
  const [assigning, setAssigning] = useState(false);
  const { showCreateHub, setShowCreateHub } = useCreateContext();
  const hasFetched = useRef(false);
  const {
    hubManagers,
    loading: hubManagersLoading,
    error: hubManagersError,
  } = useHubManagers();
  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const [citiesMaster, setCitiesMaster] = useState<
    Array<{ city_id: number; city_name: string; state_id: number }>
  >([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [rawHubs, setRawHubs] = useState<any[]>([]);

  const { user } = useAppSelector((state) => state.auth);
  const userHubId = getHubId();
  const isSuperAdmin = user?.roleid === 1;
  const hasEditPermission = user?.roleid && [1, 2, 7, 10].includes(user.roleid);
  const hasDeletePermission = user?.roleid && [1, 2].includes(user.roleid);

  const {
    options: allStatusOptions,
    loading: statusLoading,
    error: statusError,
  } = useCodeCommon("HubStatus");

  useEffect(() => {
    const state = location.state as any;
    if (state) {
      setSearchTerm(state.searchTerm ?? "");
      setCity(state.city ?? "");
      setStatus(state.status ?? "");
      setHubManager(state.hubManager ?? "");
      setHub(state.hub ?? "");
    }
  }, [location.state]);

  const statusOptions = allStatusOptions.filter(
    (option) =>
      option.codedisplayname === "Active" ||
      option.codedisplayname === "Suspended",
  );

  const filterHubsByRole = (hubsList: HubData[]) => {
    if (isSuperAdmin) {
      return hubsList;
    } else if (userHubId !== null) {
      const filtered = hubsList.filter(
        (hub) => hub.id === userHubId.toString(),
      );
      return filtered;
    } else {
      return [];
    }
  };

  const filteredHubs = filterHubsByRole(hubs).filter((hubItem) => {
    const matchesName =
      !searchTerm ||
      (hubItem.hubName || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesManager = !hubManager || hubItem.hubManager === hubManager;
    const matchesCity = !city || hubItem.city === city;
    const matchesStatus = !status || hubItem.status === status;
    const matchesHub = !hub || hubItem.hubName === hub;
    return (
      matchesName &&
      matchesManager &&
      matchesCity &&
      matchesStatus &&
      matchesHub
    );
  });

  const pagination = usePagination({
    totalItems: filteredHubs.length,
    initialPage: getInitialPage(),
    initialRowsPerPage: 10,
  });
  const paginatedUsers = pagination.paginatedData(filteredHubs);

  const getHubManagerName = (hubmanagerid: string): string => {
    if (!hubmanagerid || !hubManagers.length) {
      return "N/A";
    }

    const manager = hubManagers.find(
      (manager) => manager.userid?.toString() === hubmanagerid.toString(),
    );

    return manager?.name || "N/A";
  };

  const getHubManagerPhone = (hubmanagerid: string): string => {
    if (!hubmanagerid || !hubManagers.length) {
      return "N/A";
    }

    const manager = hubManagers.find(
      (manager) => manager.userid?.toString() === hubmanagerid.toString(),
    );
    return manager?.phoneNumber || "N/A";
  };

  const columns: SortableColumn<HubData>[] = [
    { id: "id", label: "Hub ID" },
    { id: "hubName", label: "Hub Name" },
    {
      id: "hubManager",
      label: "Hub Manager",
      render: (row: HubData) => (
        <Typography variant="body2">{row.hubManager || "N/A"}</Typography>
      ),
    },
    { id: "mobileNo", label: "Mobile No." },
    { id: "city", label: "City" },
    {
      id: "status",
      label: "Status",
      align: "center",
      render: (row: HubData) => (
        <StatusChip status={row.status} statusOptions={statusOptions} />
      ),
    },
    { id: "operatingHours", label: "Operating Hours" },
    { id: "totalVehicles", label: "Active Vehicles", align: "center" },
    { id: "availableVehicles", label: "Available Vehicles", align: "center" },
    { id: "bookedVehicles", label: "Booked Vehicles", align: "center" },
    {
      id: "maintenanceVehicles",
      label: "Maintenance Vehicles",
      align: "center",
    },
    {
      id: "actions" as keyof HubData,
      label: "Actions",
      align: "center",
      sortable: false,
      render: (row: HubData) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
          <Button
            variant="text"
            sx={{ p: 0, minWidth: "auto" }}
            onClick={() => handleViewHubDetails(row.id)}
          >
            <img src={ActionIcon} alt="View Details" width={30} height={30} />
          </Button>
          {hasEditPermission && (
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto" }}
              onClick={() => handleEditHub(row.id)}
            >
              <img src={EditIcon} alt="Edit" width={30} height={30} />
            </Button>
          )}
          {hasDeletePermission && (
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto" }}
              onClick={() => handleDeleteHub(row.id)}
            >
              <img src={DeleteIcon} alt="Delete" width={30} height={30} />
            </Button>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (!hasFetched.current && statusOptions.length > 0) {
      hasFetched.current = true;
      fetchMasterCities();
      fetchHubs();
    }
  }, [statusOptions.length]);

  useEffect(() => {
    const shouldRefresh = (location.state as any)?.refreshHubs;
    if (shouldRefresh) {
      fetchHubs();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    try {
      const exportFilters = {
        hub,
        hubManager,
        city,
        status,
      };
      localStorage.setItem("hubExportFilters", JSON.stringify(exportFilters));
    } catch {}
  }, [hub, hubManager, city, status]);

  useEffect(() => {
    if (
      statusOptions.length > 0 &&
      rawHubs.length > 0 &&
      citiesMaster.length > 0
    ) {
      const mapped = rawHubs.map((hub: any, index: number) => {
        const getStatusText = (status: any) => {
          const statusOption = statusOptions.find(
            (option) => option.codevalue === String(status),
          );
          return statusOption
            ? statusOption.codedisplayname
            : String(status) || "";
        };

        const hubmasterid = hub.hubmasterid?.toString();
        const hubmanagerid = hub.hubmanagerid?.toString();
        const hubCityId = parseInt(hub.city?.toString());
        const hubStateId = parseInt(hub.state?.toString());
        const matchedCity = citiesMaster.find(
          (c) =>
            Number(c.city_id) === hubCityId &&
            Number(c.state_id) === hubStateId,
        );
        const cityDisplay = matchedCity?.city_name;

        return {
          id: hubmasterid,
          hubName: hub.hubname || "",
          hubManager: getHubManagerName(hubmanagerid),
          mobileNo: getHubManagerPhone(hubmanagerid),
          city: cityDisplay || "N/A",
          status: getStatusText(hub.status),
          operatingHours: hub.operationhours || "N/A",
          totalVehicles:
            hub.activeVehicleCount !== undefined
              ? hub.activeVehicleCount
              : "N/A",
          availableVehicles:
            hub.availableVehicleCount !== undefined
              ? hub.availableVehicleCount
              : "N/A",
          bookedVehicles:
            hub.bookedVehicleCount !== undefined
              ? hub.bookedVehicleCount
              : "N/A",
          maintenanceVehicles:
            hub.underMaintenanceVehicleCount !== undefined
              ? hub.underMaintenanceVehicleCount
              : "N/A",
        };
      });

      setHubs(mapped);
    }
  }, [
    statusOptions.length,
    rawHubs.length,
    citiesMaster.length,
    hubManagers.length,
  ]);

  const fetchMasterCities = async () => {
    setCitiesLoading(true);
    try {
      const response = await apiService.getCities();
      const data = (response as any)?.data || response;
      let list: any[] = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray((data as any).result))
        list = (data as any).result;
      else if (data && Array.isArray((data as any).cities))
        list = (data as any).cities;
      setCitiesMaster(list);
    } catch (e) {
      setCitiesMaster([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  const fetchHubs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getAllHubs();

      if (response) {
        let transformedHubs = [];
        const responseData = (response as any)?.data || response;

        if (Array.isArray(responseData)) {
          transformedHubs = responseData;
        } else if (responseData && typeof responseData === "object") {
          if (Array.isArray(responseData.hubs)) {
            transformedHubs = responseData.hubs;
          } else if (Array.isArray(responseData.result)) {
            transformedHubs = responseData.result;
          }
        }

        if (transformedHubs.length > 0) {
          setRawHubs(transformedHubs);
        } else {
          setRawHubs([]);
          setHubs([]);
        }
      } else {
        setError("Invalid response from server");
        setRawHubs([]);
        setHubs([]);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch hubs");
      setRawHubs([]);
      setHubs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHubDetails = async (hubId: string) => {
    try {
      setLoading(true);
      navigate(`/hub-management/hub-details/${hubId}`, {
        state: {
          searchTerm,
          city,
          status,
          hubManager,
          hub,
          page: pagination.page,
        },
      });
    } catch (err: any) {
      setError(err.message || "Failed to navigate to hub details");
    } finally {
      setLoading(false);
    }
  };

  const handleEditHub = async (hubId: string) => {
    try {
      setLoading(true);
      navigate(`/hub-management/edit-hub/${hubId}`, {
        state: {
          searchTerm,
          city,
          status,
          hubManager,
          hub,
          page: pagination.page,
        },
      });
    } catch (err: any) {
      setError(err.message || "Failed to navigate to edit hub");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHub = (hubId: string) => {
    setHubToDelete(hubId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!hubToDelete) return;

    setDeleting(true);
    try {
      await apiService.deleteHub(hubToDelete);
      await fetchHubs();
      setDeleteDialogOpen(false);
      setHubToDelete(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete hub");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setHubToDelete(null);
  };

  const handleAssignManager = (hubId: string) => {
    setSelectedHubId(hubId);
    setSelectedManagerId("");
    setAssignManagerModalOpen(true);
  };

  const handleAssignManagerSave = async () => {
    if (!selectedHubId || !selectedManagerId) return;

    setAssigning(true);
    try {
      const manager = hubManagers.find(
        (m) => m.userid.toString() === selectedManagerId,
      );
      if (!manager) {
        setError("Selected manager not found");
        return;
      }
      const response = await apiService.getHubById(selectedHubId);
      const hubData = (response as any)?.data || response;

      if (!hubData) {
        setError("Failed to fetch hub data");
        return;
      }

      const updatePayload = {
        hubmasterid: parseInt(selectedHubId),
        hubmanagerid: manager.userid,
        userid: 0,
        hubname: hubData.hubname || hubData.hubName || hubData.name || "",
        latitude:
          parseFloat(
            hubData.latitude?.toString() || hubData.lat?.toString() || "0",
          ) || 0,
        longitude:
          parseFloat(
            hubData.longitude?.toString() ||
              hubData.lng?.toString() ||
              hubData.lon?.toString() ||
              "0",
          ) || 0,
        address1: hubData.address1 || hubData.address || "",
        address2: hubData.address2 || "",
        city:
          parseInt(
            hubData.city?.toString() || hubData.cityId?.toString() || "0",
          ) || 0,
        state:
          parseInt(
            hubData.state?.toString() || hubData.stateId?.toString() || "0",
          ) || 0,
        country: 0,
        zipcode: hubData.zipcode || hubData.pincode || hubData.postalCode || "",
        status:
          parseInt(
            hubData.status?.toString() || hubData.isActive?.toString() || "0",
          ) || 0,
        operationhours:
          hubData.operationhours ||
          hubData.operatingHours ||
          hubData.hours ||
          "",
        geofencestartradius:
          parseFloat(
            hubData.geofencestartradius?.toString() ||
              hubData.geofenceRadius?.toString() ||
              hubData.radius?.toString() ||
              "0",
          ) || 0,
        geofencesendradius: 0,
        phonenumber:
          manager.phoneNumber ||
          hubData.phonenumber ||
          hubData.phoneNumber ||
          hubData.phone ||
          hubData.mobile ||
          "",
        whatsappnumber: "",
        hubimage:
          hubData.hubimage ||
          hubData.image ||
          hubData.hubImage ||
          hubData.photo ||
          "",
      };

      await apiService.updateHub(selectedHubId, updatePayload);

      await fetchHubs();

      setAssignManagerModalOpen(false);
      setSelectedHubId(null);
      setSelectedManagerId("");
    } catch (err: any) {
      setError(err.message || "Failed to assign manager");
    } finally {
      setAssigning(false);
    }
  };

  const cancelAssignManager = () => {
    setAssignManagerModalOpen(false);
    setSelectedHubId(null);
    setSelectedManagerId("");
  };

  const getAvailableHubNames = () => {
    let filteredHubs = filterHubsByRole(hubs);

    if (hubManager) {
      filteredHubs = filteredHubs.filter((h) => h.hubManager === hubManager);
    }

    if (city) {
      filteredHubs = filteredHubs.filter((h) => h.city === city);
    }

    return Array.from(new Set(filteredHubs.map((h) => h.hubName))).sort();
  };

  const getAvailableHubManagers = () => {
    let filteredHubs = filterHubsByRole(hubs);

    if (hub) {
      filteredHubs = filteredHubs.filter((h) => h.hubName === hub);
    }

    if (city) {
      filteredHubs = filteredHubs.filter((h) => h.city === city);
    }

    const managerNames = Array.from(
      new Set(filteredHubs.map((h) => h.hubManager)),
    ).sort();
    return managerNames;
  };

  const getAvailableCities = () => {
    const cityNames = Array.from(
      new Set(citiesMaster.map((c) => c.city_name)),
    ).sort();
    return cityNames.map((cityName) => ({
      label: cityName,
      value: cityName,
    }));
  };

  const handleHubChange = (value: string) => {
    pagination.goToPage(0);
    const hubsForSelectedHub = hubs.filter((h) => {
      const roleFiltered = filterHubsByRole([h]);
      return roleFiltered.length > 0 && roleFiltered[0].hubName === value;
    });

    const managers = Array.from(
      new Set(hubsForSelectedHub.map((h) => h.hubManager)),
    );
    const cities = Array.from(new Set(hubsForSelectedHub.map((h) => h.city)));

    const newHub = value;
    let newHubManager = hubManager;
    let newCity = city;

    const shouldClearManager = hubManager && !managers.includes(hubManager);
    const shouldClearCity = city && !cities.includes(city);

    if (shouldClearManager) {
      newHubManager = "";
    }
    if (shouldClearCity) {
      newCity = "";
    }

    if (managers.length === 1 && !shouldClearManager) {
      newHubManager = managers[0];
    }
    if (cities.length === 1 && !shouldClearCity) {
      newCity = cities[0];
    }

    setHub(newHub);
    setHubManager(newHubManager);
    setCity(newCity);

    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        hub: newHub,
        hubManager: newHubManager,
        city: newCity,
        page: 0,
      },
    });
  };

  const handleHubManagerChange = (value: string) => {
    pagination.goToPage(0);
    const hubsForSelectedManager = hubs.filter((h) => {
      const roleFiltered = filterHubsByRole([h]);
      return roleFiltered.length > 0 && roleFiltered[0].hubManager === value;
    });

    const hubNames = Array.from(
      new Set(hubsForSelectedManager.map((h) => h.hubName)),
    );
    const cities = Array.from(
      new Set(hubsForSelectedManager.map((h) => h.city)),
    );

    const newHubManager = value;
    let newHub = hub;
    let newCity = city;

    const shouldClearHub = hub && !hubNames.includes(hub);
    const shouldClearCity = city && !cities.includes(city);

    if (shouldClearHub) {
      newHub = "";
    }
    if (shouldClearCity) {
      newCity = "";
    }

    if (hubNames.length === 1 && !shouldClearHub) {
      newHub = hubNames[0];
    }
    if (cities.length === 1 && !shouldClearCity) {
      newCity = cities[0];
    }

    setHubManager(newHubManager);
    setHub(newHub);
    setCity(newCity);

    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        hubManager: newHubManager,
        hub: newHub,
        city: newCity,
        page: 0,
      },
    });
  };

  const handleCityChange = (value: string) => {
    pagination.goToPage(0);
    const hubsForSelectedCity = hubs.filter((h) => {
      const roleFiltered = filterHubsByRole([h]);
      return roleFiltered.length > 0 && roleFiltered[0].city === value;
    });

    const hubNames = Array.from(
      new Set(hubsForSelectedCity.map((h) => h.hubName)),
    );
    const managers = Array.from(
      new Set(hubsForSelectedCity.map((h) => h.hubManager)),
    );

    const newCity = value;
    let newHub = hub;
    let newHubManager = hubManager;

    const shouldClearHub = hub && !hubNames.includes(hub);
    const shouldClearManager = hubManager && !managers.includes(hubManager);

    if (shouldClearHub) {
      newHub = "";
    }
    if (shouldClearManager) {
      newHubManager = "";
    }

    if (hubNames.length === 1 && !shouldClearHub) {
      newHub = hubNames[0];
    }
    if (managers.length === 1 && !shouldClearManager) {
      newHubManager = managers[0];
    }

    setCity(newCity);
    setHub(newHub);
    setHubManager(newHubManager);

    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        city: newCity,
        hub: newHub,
        hubManager: newHubManager,
        page: 0,
      },
    });
  };

  if (location.pathname === "/hub-management/create-hub") {
    return <CreateHubPage onBack={() => setShowCreateHub(false)} />;
  }

  return (
    <>
      <Container fluid className="px-0">
        {(error || hubManagersError) && (
          <Alert
            severity="error"
            className="mb-3"
            onClose={() => setError(null)}
          >
            {error || hubManagersError}
          </Alert>
        )}

        <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
          <Row className="g-3 align-items-end">
            {isSuperAdmin && (
              <>
                <Col xs={12} md={2}>
                  <SearchableSelectInput
                    field="hub"
                    formData={{ hub }}
                    onChange={(_, value) => handleHubChange(value)}
                    options={getAvailableHubNames()}
                    label="Hub Name"
                    placeholder="Search and select hub"
                    disabled={hubsLoading}
                  />
                </Col>

                <Col xs={12} md={2}>
                  <SearchableSelectInput
                    field="hubManager"
                    formData={{ hubManager }}
                    onChange={(_, value) => handleHubManagerChange(value)}
                    options={getAvailableHubManagers()}
                    label="Hub Manager"
                    placeholder="Search and select manager"
                    disabled={hubManagersLoading}
                  />
                </Col>
              </>
            )}

            <Col xs={12} md={2}>
              <SearchableSelectInput
                field="city"
                formData={{ city }}
                onChange={(_, value) => handleCityChange(value)}
                label="City"
                placeholder="Select city"
                options={getAvailableCities()}
                disabled={citiesLoading}
              />
            </Col>

            <Col xs={12} md={2}>
              <SearchableSelectInput
                field="status"
                formData={{ status: status || "" }}
                onChange={(_, value) => {
                  setStatus(value);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...((location.state as object) || {}),
                      status: value,
                      page: 0,
                    },
                  });
                }}
                label="Status"
                placeholder="Select status"
                options={statusOptions.map((statusOption) => ({
                  label: statusOption.codedisplayname,
                  value: statusOption.codedisplayname,
                }))}
                disabled={statusLoading}
              />
            </Col>

            <Col xs={12} md={2} className="d-flex gap-2 justify-content-end" />
          </Row>
        </Paper>

        <Paper className="border-0 rounded-0 shadow-none">
          {loading || hubManagersLoading || hubsLoading ? (
            <CustomLoading message="Fetching Hubs Table Data..." />
          ) : (
            <SortableTable
              data={paginatedUsers}
              columns={columns}
              emptyMessage="No hubs found"
            />
          )}
        </Paper>
      </Container>
      <Pagination
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        totalItems={filteredHubs.length}
        totalPages={pagination.totalPages}
        startItem={pagination.startItem}
        endItem={pagination.endItem}
        rowsPerPageOptions={[5, 10, 25, 50]}
        showRowsPerPage
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        onGoToNextPage={pagination.goToNextPage}
        onGoToPreviousPage={pagination.goToPreviousPage}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this hub? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignManagerModalOpen}
        onClose={cancelAssignManager}
        aria-labelledby="assign-manager-dialog-title"
        aria-describedby="assign-manager-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="assign-manager-dialog-title">
          Assign Hub Manager
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="assign-manager-dialog-description"
            sx={{ mb: 2 }}
          >
            Select a manager to assign to this hub.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel>Select Manager</InputLabel>
            <Select
              value={selectedManagerId}
              label="Select Manager"
              onChange={(e) => setSelectedManagerId(e.target.value)}
            >
              {hubManagers.map((manager) => (
                <MenuItem
                  key={manager.userid}
                  value={manager.userid.toString()}
                >
                  {manager.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelAssignManager} disabled={assigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignManagerSave}
            color="primary"
            variant="contained"
            disabled={assigning || !selectedManagerId}
            startIcon={assigning ? <CircularProgress size={16} /> : null}
          >
            {assigning ? "Assigning..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HubManagementPage;
